import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  Bell,
  Bot,
  Brain,
  ChevronRight,
  Clock3,
  LayoutGrid,
  Search,
  Send,
  Sparkles,
  Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Badge, PageShell, SectionCard } from '../components/PeecUI';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

type MessageRole = 'user' | 'assistant';
type AgentStatus = 'idle' | 'running' | 'done';

interface SourceCitation {
  title: string;
  domain: string;
  relevance: number;
}

interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: Date;
  sources?: SourceCitation[];
}

interface AgentProgress {
  status: AgentStatus;
  currentTaskIndex: number;
}

interface AgentDefinition {
  id: string;
  name: string;
  title: string;
  subtitle: string;
  icon: LucideIcon;
  gradient: string;
  accent: string;
  badgeTone: 'blue' | 'green' | 'amber' | 'purple';
  tasks: string[];
}

const AGENTS: AgentDefinition[] = [
  {
    id: 'analyst',
    name: 'Ava',
    title: '数据分析师',
    subtitle: '负责多引擎检索与参数拆解',
    icon: Search,
    gradient: 'from-sky-500 to-blue-500',
    accent: 'bg-sky-500',
    badgeTone: 'blue',
    tasks: ['解析用户意图与参数', '执行多引擎搜索', '提取可见性指标'],
  },
  {
    id: 'auditor',
    name: 'Mia',
    title: '品牌审计师',
    subtitle: '聚合品牌提及与情感表现',
    icon: Brain,
    gradient: 'from-emerald-500 to-lime-500',
    accent: 'bg-emerald-500',
    badgeTone: 'green',
    tasks: ['执行情感分析', '聚合品牌提及', '计算 GEO 得分'],
  },
  {
    id: 'strategist',
    name: 'Leo',
    title: '内容策略师',
    subtitle: '识别竞品引用与优化机会',
    icon: Workflow,
    gradient: 'from-amber-500 to-orange-500',
    accent: 'bg-amber-500',
    badgeTone: 'amber',
    tasks: ['分析竞品内容', '识别引用缺口', '优先排序策略'],
  },
  {
    id: 'synthesizer',
    name: 'Zane',
    title: '综合协调员',
    subtitle: '整合结果并输出最终建议',
    icon: Sparkles,
    gradient: 'from-violet-500 to-fuchsia-500',
    accent: 'bg-violet-500',
    badgeTone: 'purple',
    tasks: ['交叉引用洞察', '生成引证回复', '输出最终报告'],
  },
];

const DEFAULT_MESSAGES: ChatMessage[] = [
  {
    id: 'welcome',
    role: 'assistant',
    content:
      '您好！我是 GEO Agent 协调员。请发送您的提示词，我将调度专业团队（Ava、Mia、Leo 和 Zane）分析您的品牌可见度并制定优化策略。',
    timestamp: new Date(Date.now() - 60_000),
  },
];

const DEFAULT_PROGRESS: Record<string, AgentProgress> = {
  analyst: { status: 'running', currentTaskIndex: 2 },
  auditor: { status: 'running', currentTaskIndex: 1 },
  strategist: { status: 'running', currentTaskIndex: 1 },
  synthesizer: { status: 'running', currentTaskIndex: 0 },
};

const COMPLETED_PROGRESS: Record<string, AgentProgress> = {
  analyst: { status: 'done', currentTaskIndex: 2 },
  auditor: { status: 'done', currentTaskIndex: 2 },
  strategist: { status: 'done', currentTaskIndex: 2 },
  synthesizer: { status: 'done', currentTaskIndex: 2 },
};

const PANEL_CLASS = 'overflow-hidden rounded-none bg-white/70 shadow-none [&>div:first-child]:border-b-0';

const ADVANTAGES = [
  { title: '并行处理', desc: '提升 4 倍效率', icon: Brain },
  { title: '专业分工', desc: '深度专业分析', icon: LayoutGrid },
  { title: '实时协同', desc: '动态同步推进', icon: Workflow },
  { title: '智能整合', desc: '输出最优结果', icon: Sparkles },
];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function getAgentPercent(agent: AgentDefinition, progress: AgentProgress) {
  if (progress.status === 'idle') {
    return 0;
  }

  if (progress.status === 'done') {
    return 100;
  }

  return Math.round(((progress.currentTaskIndex + 1) / agent.tasks.length) * 100);
}

function getStatusMeta(status: AgentStatus) {
  if (status === 'done') {
    return { label: '已完成', tone: 'green' as const };
  }

  if (status === 'running') {
    return { label: '运行中', tone: 'green' as const };
  }

  return { label: '待命中', tone: 'slate' as const };
}

function buildTaskSummary(progressMap: Record<string, AgentProgress>) {
  return AGENTS.reduce(
    (summary, agent) => {
      const progress = progressMap[agent.id];

      if (progress.status === 'done') {
        summary.done += agent.tasks.length;
      } else if (progress.status === 'idle') {
        summary.waiting += agent.tasks.length;
      } else {
        summary.done += progress.currentTaskIndex;
        summary.running += 1;
        summary.waiting += agent.tasks.length - progress.currentTaskIndex - 1;
      }

      summary.total += agent.tasks.length;
      return summary;
    },
    { running: 0, waiting: 0, done: 0, total: 0 },
  );
}

const ProgressRing: React.FC<{ done: number; total: number }> = ({ done, total }) => {
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);

  return (
    <div className="relative mx-auto h-24 w-24">
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: `conic-gradient(rgb(59 130 246) 0 ${percent}%, rgb(245 158 11) ${percent}% ${Math.min(percent + 16, 100)}%, rgb(229 231 235) 0 100%)`,
        }}
      />
      <div className="absolute inset-[10px] rounded-full bg-white" />
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-semibold tracking-tight text-zinc-950">{total}</span>
        <span className="text-xs text-zinc-400">总任务数</span>
      </div>
    </div>
  );
};

const ConsoleBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <div className={`rounded-2xl border px-4 py-3 ${isUser ? 'border-zinc-200 bg-white' : 'border-zinc-200 bg-zinc-50'}`}>
      <div className="mb-2 flex items-center gap-2 text-xs text-zinc-400">
        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${isUser ? 'bg-zinc-900 text-white' : 'bg-gradient-to-br from-violet-500 to-blue-500 text-white'}`}>
          <Bot size={12} aria-hidden="true" />
        </span>
        <span>{isUser ? '您的请求' : '协调员'}</span>
      </div>
      <p className="text-sm leading-6 text-zinc-700">{message.content}</p>
      {message.sources && message.sources.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {message.sources.map((source) => (
            <span key={source.title} className="inline-flex items-center rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-[11px] text-zinc-500">
              {source.domain}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const GeoAgentPage: React.FC<{ workspaceId: string }> = ({ workspaceId: _workspaceId }) => {
  const [messages, setMessages] = useState<ChatMessage[]>(DEFAULT_MESSAGES);
  const [inputValue, setInputValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [agentProgress, setAgentProgress] = useState<Record<string, AgentProgress>>(DEFAULT_PROGRESS);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const latestMessages = messages.slice(-2);
  const taskSummary = useMemo(() => buildTaskSummary(agentProgress), [agentProgress]);
  const anyRunning = useMemo(() => Object.values(agentProgress).some((item) => item.status === 'running'), [agentProgress]);

  const recentActivity = useMemo(
    () => [
      {
        agent: 'Ava',
        text: anyRunning ? '完成需求拆解' : '完成检索任务',
        time: '2 分钟前',
        tone: 'blue' as const,
      },
      {
        agent: 'Mia',
        text: anyRunning ? '聚合品牌提及及情感倾向' : '完成品牌提及聚合',
        time: '5 分钟前',
        tone: 'green' as const,
      },
      {
        agent: 'Leo',
        text: '生成优化建议',
        time: '8 分钟前',
        tone: 'amber' as const,
      },
      {
        agent: 'Zane',
        text: '生成最终报告',
        time: '12 分钟前',
        tone: 'purple' as const,
      },
    ],
    [anyRunning],
  );

  const runWorkflow = useCallback(async (userQuery: string) => {
    void userQuery;

    setAgentProgress({
      analyst: { status: 'running', currentTaskIndex: 0 },
      auditor: { status: 'idle', currentTaskIndex: 0 },
      strategist: { status: 'idle', currentTaskIndex: 0 },
      synthesizer: { status: 'idle', currentTaskIndex: 0 },
    });

    for (let index = 0; index < 3; index += 1) {
      setAgentProgress((current) => ({
        ...current,
        analyst: { status: 'running', currentTaskIndex: index },
      }));
      await sleep(320);
    }

    setAgentProgress({
      analyst: { status: 'done', currentTaskIndex: 2 },
      auditor: { status: 'running', currentTaskIndex: 0 },
      strategist: { status: 'running', currentTaskIndex: 0 },
      synthesizer: { status: 'idle', currentTaskIndex: 0 },
    });

    for (let index = 0; index < 3; index += 1) {
      setAgentProgress((current) => ({
        ...current,
        auditor: { status: 'running', currentTaskIndex: index },
        strategist: { status: 'running', currentTaskIndex: index },
      }));
      await sleep(360);
    }

    setAgentProgress({
      analyst: { status: 'done', currentTaskIndex: 2 },
      auditor: { status: 'done', currentTaskIndex: 2 },
      strategist: { status: 'done', currentTaskIndex: 2 },
      synthesizer: { status: 'running', currentTaskIndex: 0 },
    });

    for (let index = 0; index < 3; index += 1) {
      setAgentProgress((current) => ({
        ...current,
        synthesizer: { status: 'running', currentTaskIndex: index },
      }));
      await sleep(320);
    }

    setAgentProgress(COMPLETED_PROGRESS);
  }, []);

  const handleSend = useCallback(async () => {
    const trimmed = inputValue.trim();

    if (!trimmed || isGenerating) {
      return;
    }

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };

    setMessages((current) => [...current, userMessage]);
    setInputValue('');
    setIsGenerating(true);

    await runWorkflow(trimmed);

    setMessages((current) => [
      ...current,
      {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content:
          '已完成多智能体分析。当前品牌在 GEO 视角下的核心机会集中在 FAQ 结构化、竞品引用反超以及高意图话题补位，建议优先处理定价页与对比页内容。',
        timestamp: new Date(),
        sources: [
          { title: 'GEO 基准报告', domain: 'searchengineland.com', relevance: 92 },
          { title: '品牌可见度指数', domain: 'brightedge.com', relevance: 88 },
        ],
      },
    ]);
    setIsGenerating(false);
  }, [inputValue, isGenerating, runWorkflow]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        void handleSend();
      }
    },
    [handleSend],
  );

  return (
    <PageShell wide>
      <div 
        className="-mx-5 -mt-7 -mb-7 flex h-screen flex-col overflow-hidden bg-gradient-to-br from-violet-50 via-indigo-50 to-fuchsia-100 px-5 pt-3 pb-3 sm:-mx-8 sm:px-8"
      >
        <div className="flex-none bg-contain bg-center bg-no-repeat -mx-5 -mt-3 sm:-mx-8 px-5 pt-3 sm:px-8" style={{ backgroundImage: 'url(/assets/img/GEO_AEGNT_PAGE_TITLE.png)' }}>
          <header className="flex-none flex flex-wrap items-center justify-between gap-3 text-sm">
            <div className="flex min-w-0 items-center gap-2 font-medium text-zinc-500">
              <span className="truncate text-zinc-400">AI 工作台</span>
              <span className="text-zinc-300">/</span>
              <span className="truncate text-zinc-950 py-5">GEO 智能体集群</span>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <LanguageSwitcher />
              <div className="flex items-center gap-1.5 leading-none select-none">
                <span className="text-xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-zinc-800 to-zinc-500">
                  元基
                </span>
                <span className="text-xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-cyan-500">
                  GEO
                </span>
                <span className="inline-flex items-center justify-center rounded-bl-lg rounded-br-sm rounded-tl-sm rounded-tr-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 px-1.5 py-1 text-[9px] font-bold uppercase leading-none text-white shadow-sm ring-1 ring-inset ring-white/20">
                  AI
                </span>
              </div>
              <button
                type="button"
                aria-label="查看通知"
                className="relative inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/70 text-zinc-600 transition-colors hover:bg-white focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
              >
                <Bell size={15} aria-hidden="true" />
                <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-rose-500" />
              </button>
              <div className="h-8 w-8 overflow-hidden rounded-full bg-white/80 p-1 shadow-sm" aria-hidden="true">
                <div className="flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-zinc-900 to-zinc-500 text-[11px] font-semibold text-white">
                  AI
                </div>
              </div>
            </div>
          </header>

          <div className="flex-none relative pt-3 pb-8 pl-4 pr-5 sm:pr-8">
          <Sparkles className="absolute right-2 top-3 text-violet-300" size={30} aria-hidden="true" />
          <div className="flex items-center gap-4">
            <h1 className="text-balance text-4xl font-bold tracking-tight text-zinc-950 sm:text-5xl">
              <span className="bg-gradient-to-r from-violet-600 to-blue-500 bg-clip-text text-transparent">GEO</span>{' '}
              Agent Swarm
            </h1>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/55 px-3 py-1 text-xs font-medium text-emerald-700 shadow-sm">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              AI 协同中
            </div>
          </div>
          <p className="mt-2 text-sm text-zinc-500">
            多智能体协同工作，洞察 GEO 机会，驱动品牌增长。
          </p>
        </div>
        </div>

      <div className="flex-1 min-h-0 auto-rows-fr grid gap-3 xl:grid-cols-[340px_minmax(0,1fr)_300px]">
        <SectionCard
          className={`h-full flex flex-col ${PANEL_CLASS}`}
          title="对话控制台"
          subtitle="4 个 AI 代理协同工作"
          action={<Badge tone="green">已连接</Badge>}
        >
          <div className="flex-1 min-h-0 overflow-y-auto space-y-2 px-4 py-3">
            {latestMessages.map((message) => (
              <ConsoleBubble key={message.id} message={message} />
            ))}

            <div className="rounded-[24px] bg-[radial-gradient(circle_at_top,rgba(238,242,255,0.95),rgba(255,255,255,1))] px-3 py-3 shadow-sm">
              <div className="mb-2 flex items-center justify-center">
                <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-[radial-gradient(circle_at_top,rgba(255,255,255,1),rgba(224,231,255,0.9),rgba(216,180,254,0.35))] shadow-[0_14px_32px_rgba(99,102,241,0.16)]">
                  <Bot size={32} className="text-violet-500" aria-hidden="true" />
                  <span className="absolute right-0 top-2 inline-flex items-center rounded-full bg-blue-500 px-2 py-1 text-[11px] font-medium text-white shadow-sm">
                    Hi~
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  '品牌可见性分析',
                  '竞品对比洞察',
                  '内容优化建议',
                  '自定义分析',
                ].map((item) => (
                  <button
                    key={item}
                    type="button"
                    className="rounded-xl bg-white px-2.5 py-2 text-left text-xs font-medium text-zinc-600 shadow-sm transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex-none border-t border-zinc-100 px-4 py-3">
            <label htmlFor="geo-agent-input" className="sr-only">
              输入分析需求
            </label>
            <div className="rounded-2xl bg-white p-3 shadow-sm focus-within:ring-2 focus-within:ring-zinc-300">
              <div className="flex items-end gap-3">
                <textarea
                  ref={inputRef}
                  id="geo-agent-input"
                  name="geoAgentInput"
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="输入您的分析需求…"
                  autoComplete="off"
                  spellCheck={false}
                  rows={2}
                  className="min-h-[40px] flex-1 resize-none bg-transparent text-sm leading-6 text-zinc-700 outline-none placeholder:text-zinc-400"
                />
                <button
                  type="button"
                  onClick={() => {
                    void handleSend();
                  }}
                  disabled={!inputValue.trim() || isGenerating}
                  aria-label="发送分析需求"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-r from-violet-500 to-blue-500 text-white transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Send size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
            <p className="mt-2 text-center text-[11px] text-zinc-400">Enter 发送 · Shift+Enter 换行</p>
          </div>
        </SectionCard>

        <SectionCard
          className={`h-full flex flex-col ${PANEL_CLASS}`}
          title="智能体集群活动"
          subtitle="智能体任务链路实时推进"
          action={
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 shadow-sm transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
            >
              全视任务
              <ArrowRight size={12} aria-hidden="true" />
            </button>
          }
        >
          <div className="flex-1 min-h-0 overflow-y-auto space-y-2 px-3 py-3">
            {AGENTS.map((agent) => {
              const progress = agentProgress[agent.id];
              const percent = getAgentPercent(agent, progress);
              const statusMeta = getStatusMeta(progress.status);
              const Icon = agent.icon;

              return (
                <article
                  key={agent.id}
                  className={`rounded-2xl px-3 py-2 shadow-sm transition-colors ${progress.status === 'running' ? 'bg-zinc-50/80' : 'bg-white/85'}`}
                >
                  <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)_80px] lg:items-center">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${agent.gradient} text-white shadow-sm`}>
                          <Icon size={18} aria-hidden="true" />
                        </div>
                        <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-violet-500 shadow-sm">
                          <Bot size={14} aria-hidden="true" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold tracking-tight text-zinc-950">{agent.name}</div>
                          <div className="truncate text-xs text-zinc-500">{agent.title}</div>
                        </div>
                      </div>
                      <div className="mt-1.5 flex items-center gap-3">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-200">
                          <div className={`h-full rounded-full ${agent.accent}`} style={{ width: `${percent}%` }} />
                        </div>
                        <span className="text-sm font-medium tabular-nums text-zinc-500">{percent}%</span>
                      </div>
                    </div>

                    <ul className="space-y-1">
                      {agent.tasks.map((task, index) => {
                        const isDone = progress.status === 'done' || index < progress.currentTaskIndex;
                        const isActive = progress.status === 'running' && index === progress.currentTaskIndex;

                        return (
                          <li key={task} className="flex items-center gap-2 text-xs text-zinc-600">
                            <span
                              className={`h-2 w-2 rounded-full ${isDone ? 'bg-zinc-950' : isActive ? agent.accent : 'bg-zinc-300'}`}
                              aria-hidden="true"
                            />
                            <span className={isDone ? 'text-zinc-950' : undefined}>{task}</span>
                          </li>
                        );
                      })}
                    </ul>

                    <div className="flex items-center justify-between gap-2 lg:justify-end">
                      <Badge tone={statusMeta.tone}>{statusMeta.label}</Badge>
                      <button
                        type="button"
                        aria-label={`查看 ${agent.name} 详情`}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-zinc-500 shadow-sm transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                      >
                        <ChevronRight size={16} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </SectionCard>

        <div className="flex flex-col gap-3 h-full min-h-0">
          <SectionCard className={`flex-none ${PANEL_CLASS}`}>
            <div className="bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500 px-4 py-2.5 text-white">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <LayoutGrid size={16} aria-hidden="true" />
                全局任务概览
              </div>
              <p className="mt-1 text-xs text-white/80">实时掌握任务链路推进状态</p>
            </div>

            <div className="px-4 py-3">
              <div className="flex items-center gap-3">
                <ProgressRing done={taskSummary.done} total={taskSummary.total} />
                <div className="flex-1 space-y-1.5 text-xs text-zinc-600">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-500" />运行中</span>
                    <span className="tabular-nums font-medium">{taskSummary.running}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-400" />待命中</span>
                    <span className="tabular-nums font-medium">{taskSummary.waiting}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" />已完成</span>
                    <span className="tabular-nums font-medium">{taskSummary.done}</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <div className="text-xs text-zinc-400">任务成功率</div>
                  <div className="mt-1 text-2xl font-semibold tracking-tight text-zinc-950">92.5%</div>
                  <div className="mt-1 text-xs text-emerald-600">↑ 12%</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-400">平均处理时长</div>
                  <div className="mt-1 text-2xl font-semibold tracking-tight text-zinc-950">2.4</div>
                  <div className="mt-1 text-xs text-rose-500">分钟 ↓ 8%</div>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            className={`flex-1 min-h-0 flex flex-col ${PANEL_CLASS}`}
            title="最近活动"
            subtitle="实时更新"
            action={
              <button
                type="button"
                className="text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-600 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
              >
                查看全部
              </button>
            }
          >
            <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-zinc-100 px-4">
              {recentActivity.map((item) => (
                <div key={`${item.agent}-${item.time}`} className="flex items-start gap-2 py-1.5">
                  <span className={`mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full ${item.tone === 'blue' ? 'bg-sky-50 text-sky-600' : item.tone === 'green' ? 'bg-emerald-50 text-emerald-600' : item.tone === 'amber' ? 'bg-amber-50 text-amber-600' : 'bg-violet-50 text-violet-600'}`}>
                    <Bot size={11} aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium leading-tight text-zinc-800">{item.agent}</div>
                    <div className="text-[11px] leading-tight text-zinc-500">{item.text}</div>
                  </div>
                  <div className="inline-flex items-center gap-1 text-[10px] text-zinc-400">
                    <Clock3 size={10} aria-hidden="true" />
                    {item.time}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>

      <div className="flex-none mt-2 overflow-hidden rounded-2xl">
        <div className="flex items-center gap-5 bg-gradient-to-r from-violet-600 via-indigo-500 to-blue-500 px-5 py-6">
          <div className="flex-none border-r border-white/25 pr-5">
            <div className="text-base font-bold text-white">智能体协同优势</div>
            <div className="mt-1 text-xs text-white/70">多维度分析，深度洞察，精准策略</div>
          </div>
          {ADVANTAGES.map((adv) => {
            const AdvIcon = adv.icon;
            return (
              <div key={adv.title} className="flex min-w-0 flex-1 items-center gap-3">
                <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-white/20">
                  <AdvIcon size={20} className="text-white" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold leading-tight text-white">{adv.title}</div>
                  <div className="text-[11px] leading-tight text-white/65">{adv.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      </div>
    </PageShell>
  );
};

export default GeoAgentPage;
